'use client';

import type { ICollectionPipelineStatusV3 } from '@repo/sdk';
import { matchSubtitleV1Api } from '@repo/sdk';
import type { JSX } from 'react';
import { useCallback } from 'react';
import { PipelineStepCell } from '../pipeline-step-cell';
import { PipelineStepCountBadge } from '../pipeline-step-count-badge';
import { PipelineStepDialog } from '../pipeline-step-dialog';
import { useStepAction } from '../use-step-action';
import { formatRanAt } from './format-ran-at';
import { StepLogPanel } from './step-log-panel';

interface IMatchStepProps {
  item: ICollectionPipelineStatusV3;
}

export function MatchStep({ item }: IMatchStepProps): JSX.Element {
  const { matched } = item.steps;
  const subtitleId = item.subtitleId;
  const start = useCallback(() => {
    if (subtitleId === null) {
      throw new Error('SRT not imported yet');
    }
    return matchSubtitleV1Api({ subtitleId, force: matched.ranAt !== null });
  }, [subtitleId, matched.ranAt]);
  const action = useStepAction({ start });
  const disabled = subtitleId === null ? 'SRT not imported yet' : undefined;

  return (
    <PipelineStepCell
      badge={<PipelineStepCountBadge step={matched} />}
      renderDialog={(open, onOpenChange) => (
        <PipelineStepDialog
          open={open}
          onOpenChange={onOpenChange}
          title="Match existing challenges"
          description={`Scan the subtitle for any challenge that already exists in the database and link it to this collection. ${formatRanAt(matched.ranAt)} · ${matched.count} matched.`}
          dataHref={`/sys/challenges?collectionId=${item.collectionId}&source=match`}
          dataLabel="Open linked challenges"
          primaryAction={{
            label: matched.ranAt === null ? 'Run match' : 'Re-run (force)',
            onClick: action.run,
            pending: action.status === 'running',
            disabledReason: disabled,
          }}
          body={<StepLogPanel status={action.status} log={action.log} />}
        />
      )}
    />
  );
}
